/**
 * 清單頁的過濾條件記憶（批186）——頁首的下拉、勾選與狀態籤，重新整理或切頁回來都還在。
 *
 * 用法（setup 期間呼叫，**要在註冊 watch／`usePolling` 之前**）：
 * ```ts
 * const pf = usePersistedFilters('licenses', { custFilter: '', filter: 'active' as Bucket }, { oneOf: { filter: KEYS } })
 * const { custFilter, filter } = pf.refs
 * const { dirty: filtersDirty } = pf   // ★ 物件裡的 ComputedRef 在 template 不會自動展開，要在頂層解構
 * ```
 *
 * 規則（主人 10-27 裁示＋CS 既有範式 `lib/ticketScopePrefs.ts`）：
 * - 存在 `localStorage`，鍵＝`ipo_ls_filters:v1:<email>:<role>:<page>`：帳號與角色各自一份。換角色視同新的一組，
 *   才不會讀到新角色看不到的下拉值。`email`／`role` 還沒有（測試直接 mount、`loadMe` 失敗）就只放記憶體。
 *   登出不清——同一台電腦換人登入本來就讀不到別人的鍵。
 * - **setup 當下同步讀回**再建 ref：各頁的 `watch(過濾→load)` 與 `usePolling` 都在 setup 就註冊，
 *   等到 `onMounted` 才賦值會多打一次清單。
 * - 只存與「這頁的預設」不同的欄位；全部回到預設就把鍵移除。`''` 是合法值（金鑰頁的 `''`＝全部）。
 * - 讀回的值只收預設物件裡有的鍵、型別與預設相同、通過 `oneOf`（籤／寫死的選項）——改版改了籤名，
 *   舊記憶會送出未知值、又沒有一顆籤亮起來。**不**依下拉選項清單剔除失效值：好幾個下拉本來就列不全
 *   （客戶上限 200、代理商只有 superadmin 讀得到），誤判成「清單完整」會默默丟掉合法條件；清除鈕一鍵可恢復。
 * - 深連結（`deepLink`）：網址帶了對應的 query 就**整組換成「預設＋網址的值」**並記住，再把這些 query 拿掉——
 *   否則之後改了條件再重新整理，網址會把條件蓋回去。
 * - 不監聽 `storage` 事件：別的分頁改條件不該讓這一頁莫名重載，以後寫入的為準。
 * - 記憶只是便利功能：所有 storage 存取都吞例外（無痕模式、封鎖 site data），失敗就當沒存過。
 */
import { computed, ref, shallowRef, watch, type ComputedRef, type Ref } from 'vue'
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'

import { useAuthStore } from '@/stores/auth'

export type FilterValue = string | boolean
export type FilterShape = Record<string, FilterValue>

export const FILTER_KEY_PREFIX = 'ipo_ls_filters:v1:'

export interface PersistedFilterOptions<T extends FilterShape> {
  /** 固定值域（狀態籤、寫死選項的下拉）；讀回或網址帶來的值不在裡面＝當沒有。 */
  oneOf?: { [K in keyof T]?: readonly T[K][] }
  /** query 名 → 欄位。任一個有非空值 → 整組＝預設＋網址的值、寫入記憶、再從網址拿掉這些 query。 */
  deepLink?: { route: RouteLocationNormalizedLoaded; router: Router; map: Record<string, keyof T & string> }
}

export interface PersistedFilters<T extends FilterShape> {
  refs: { [K in keyof T]: Ref<T[K]> }
  /** 有任何一欄不是這頁的預設值。 */
  dirty: ComputedRef<boolean>
  /** 這次 setup 從記憶或網址拿到值的欄位（申請頁靠它決定要不要自動挑籤）。 */
  restored: ReadonlySet<keyof T>
  /** 全部回到預設；同一個 tick 改完，多來源的陣列 watch 只會觸發一次。 */
  clear(): void
  /** 改「這頁的預設」（清除目標與 dirty 基準）；不寫記憶。給「進頁才決定預設」的頁面用。 */
  setDefaults(patch: Partial<T>): void
}

/** 記憶鍵；`email`／`role` 任一個沒有就回 null（＝只放記憶體）。 */
export function filterStorageKey(email: string | null | undefined, role: string | null | undefined, page: string): string | null {
  const e = (email ?? '').trim().toLowerCase()
  if (!e || !role) return null
  return `${FILTER_KEY_PREFIX}${e}:${role}:${page}`
}

function storage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function accepts<T extends FilterShape>(defaults: T, oneOf: PersistedFilterOptions<T>['oneOf'], k: keyof T, v: unknown): boolean {
  if (typeof v !== typeof defaults[k]) return false
  const allowed = oneOf?.[k]
  return !allowed || (allowed as readonly unknown[]).includes(v)
}

/** 讀回來的東西只信形狀對的部分；其餘一律當沒存過。 */
function sanitize<T extends FilterShape>(raw: unknown, defaults: T, oneOf: PersistedFilterOptions<T>['oneOf']): Partial<T> {
  const out: Partial<T> = {}
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out
  const obj = raw as Record<string, unknown>
  for (const k of Object.keys(defaults) as (keyof T & string)[]) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && accepts(defaults, oneOf, k, obj[k])) out[k] = obj[k] as T[typeof k]
  }
  return out
}

/** query 值 → 欄位值：陣列取第一個、空字串不算；布林欄認 `1/true/0/false`。 */
function fromQuery(v: unknown, def: FilterValue): FilterValue | undefined {
  const s = Array.isArray(v) ? v[0] : v
  if (typeof s !== 'string' || s === '') return undefined
  if (typeof def === 'boolean') {
    if (s === '1' || s === 'true') return true
    if (s === '0' || s === 'false') return false
    return undefined
  }
  return s
}

export function usePersistedFilters<T extends FilterShape>(
  page: string,
  defaults: T,
  opts: PersistedFilterOptions<T> = {},
): PersistedFilters<T> {
  const auth = useAuthStore()
  const key = filterStorageKey(auth.email, auth.role, page)
  const keys = Object.keys(defaults) as (keyof T & string)[]
  const base = shallowRef<T>({ ...defaults })

  let initial: Partial<T> = {}
  let fromLink = false
  const link = opts.deepLink
  if (link) {
    for (const [qk, field] of Object.entries(link.map)) {
      const v = fromQuery(link.route.query[qk], defaults[field])
      if (v !== undefined && accepts(defaults, opts.oneOf, field, v)) {
        initial[field] = v as T[typeof field]
        fromLink = true
      }
    }
  }
  if (!fromLink && key) {
    try {
      const raw = storage()?.getItem(key)
      if (raw) initial = sanitize(JSON.parse(raw), defaults, opts.oneOf)
    } catch {
      initial = {}
    }
  }

  const restored = new Set<keyof T>(Object.keys(initial) as (keyof T)[])
  const refs = {} as { [K in keyof T]: Ref<T[K]> }
  for (const k of keys) refs[k] = ref(k in initial ? initial[k] : defaults[k]) as Ref<T[typeof k]>

  function save() {
    if (!key) return
    const diff: Partial<T> = {}
    for (const k of keys) if (refs[k].value !== base.value[k]) diff[k] = refs[k].value
    try {
      const s = storage()
      if (!s) return
      if (Object.keys(diff).length) s.setItem(key, JSON.stringify(diff))
      else s.removeItem(key)
    } catch {
      /* 不記就是了 */
    }
  }
  watch(
    keys.map((k) => refs[k]),
    save,
  )

  if (link) {
    if (fromLink) save()
    const q = link.route.query
    if (Object.keys(link.map).some((qk) => qk in q)) {
      const rest = { ...q }
      for (const qk of Object.keys(link.map)) delete rest[qk]
      void link.router.replace({ query: rest, hash: link.route.hash }).catch(() => {})
    }
  }

  const dirty = computed(() => keys.some((k) => refs[k].value !== base.value[k]))

  function clear() {
    for (const k of keys) refs[k].value = base.value[k]
  }

  function setDefaults(patch: Partial<T>) {
    base.value = { ...base.value, ...patch }
  }

  return { refs, dirty, restored, clear, setDefaults }
}
